import { useEffect, useRef, useState } from 'react'
import { Row, Col, Badge, ListGroup, Button } from 'react-bootstrap'
import { AlertMessage, IPageProps, IRootState } from 'types'
import { V1ApiUsersClient } from 'core/http-common'
import { toast } from 'core/toast'
import { PageHeader, SaveButton, FormInput, MessageAlert, MultiFactorSelectorForm, DefaultButton, GoogleAuthenticatorSetupForm } from 'components'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Field, FormElement, FormHandle } from '@progress/kendo-react-form'
import { RequiredValidator } from 'core/Validators'
import { startLoadingAction, endLoadingAction } from 'state/actions/appActions'
import { getErrors } from 'core/helpers'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import FormSkeleton from 'components/Form/FormSkeleton'
import { MFA_LOGIN_SETTINGS_MESSAGE } from 'core/consts'
import { AppDispatch } from 'state/store'
import { UserMultiFactorSettingsJoinedDto, MultiFactorTypeEnum, DeleteMultiFactorSetupDto, MultiFactorVerifiedDto, UpdateUserPasswordDto, UpdateUserMobilePhoneDto, ValidateUserPasswordDto } from '@v1UsersApi'
import SmsMultiFactorSetupForm from 'components/MultiFactor/SmsMultiFactorSetupForm'

interface IChangePassword {
    oldPassword: string;
    newPassword: string;
    confirmNewPassword: string;
}

interface IChangeMobileNumber {
    mobilePhoneNumber: string;
}

enum MfaCallbackTypeEnum {
    Password = 1,
    DeleteGoogle = 2,
    ChangeMobileNumber = 3
}

const LoginSettingsPage = ({ title }: IPageProps) => {
    const updatePasswordFormRef = useRef<FormHandle>(null)
    const dispatch = useDispatch<AppDispatch>()

    const multiFactorGuid = useSelector((state: IRootState) => state.user.multiFactorGuid)
    const multiFactorGuidExpireDate = useSelector((state: IRootState) => state.user.multiFactorGuidExpireDate)
    const loading = useSelector((state: IRootState) => state.app.loading)

    const [alert, setAlert] = useState<AlertMessage>()
    const [userMultiFactorSettings, setUserMultiFactorSettings] = useState<Array<UserMultiFactorSettingsJoinedDto>>([])
    const [selectedMultiFactor, setSelectedMultiFactor] = useState<UserMultiFactorSettingsJoinedDto>()

    const [changePasswordFormValues, setChangePasswordFormValues] = useState<IChangePassword>()
    const [changeMobilePhoneFormValues, setChangeMobilePhoneFormValues] = useState<IChangeMobileNumber>()
    const [mfaVerifiedCallbackType, setMfaVerifiedCallbackType] = useState<MfaCallbackTypeEnum>()
    const [sms2faRequired, setSms2faRequired] = useState(false)
    const [loaded, setLoaded] = useState(false)

    const fetch = async () => {
        setMfaVerifiedCallbackType(undefined)
        setSelectedMultiFactor(undefined)
        setSms2faRequired(false)
        await dispatch(startLoadingAction())
        setTimeout(async () => {
            try {
                const results = await V1ApiUsersClient.getUserMultiFactorSettings()
                setUserMultiFactorSettings(results.data)
            }
            catch (e) {
                setAlert({ variant: 'danger', messages: getErrors(e) })
            }
            finally {
                await dispatch(endLoadingAction())
                setLoaded(true)
            }
        })
    }

    const back = () => {
        fetch()
    }

    useEffect(() => {
        fetch()
    }, [])

    useEffect(() => {
        if (!changePasswordFormValues)
            return

        if (multiFactorGuid && multiFactorGuid.trim() && multiFactorGuidExpireDate > new Date())
            updatePassword(multiFactorGuid)
        else
            setMfaVerifiedCallbackType(MfaCallbackTypeEnum.Password)
    }, [changePasswordFormValues])

    //check to make sure the password is valid before prompting MFA
    const validateUpdatePassword = async (formData: IChangePassword) => {
        await dispatch(startLoadingAction())

        if (formData.newPassword.length >= 5) {

            if (formData.newPassword == formData.confirmNewPassword) {

                try {
                    let response = await V1ApiUsersClient.validateUserPassword(new ValidateUserPasswordDto({
                        oldPassword: formData.oldPassword,
                        newPassword: formData.newPassword,
                        confirmNewPassword: formData.confirmNewPassword
                    }))

                    if (response.success)
                        setChangePasswordFormValues({ ...formData })
                } catch (e) {
                    toast.error('Old Password is incorrect. Please try again.')
                } finally {

                }
            } else {
                toast.error('New Password must match Confirm New Password. Please try again.')
            }

        } else {
            toast.error('New Password must be at least five characters long.')
        }

        await dispatch(endLoadingAction())
    }

    //Actually updates password after MFA is successful
    const updatePassword = async (multiFactorGuid: string) => {
        const formData = changePasswordFormValues
        if (!formData)
            return

        await dispatch(startLoadingAction())

        if (formData.newPassword.length >= 5) {

            if (formData.newPassword == formData.confirmNewPassword) {

                try {

                    let newPage = await V1ApiUsersClient.updateUserPassword(new UpdateUserPasswordDto({
                        oldPassword: formData.oldPassword,
                        newPassword: formData.newPassword,
                        confirmNewPassword: formData.confirmNewPassword,
                        multiFactorGuid
                    }))

                    if (newPage.success) {
                        toast.success('Password successfully changed.')
                        setMfaVerifiedCallbackType(undefined)
                        updatePasswordFormRef.current?.resetForm()
                    }

                } catch (e) {
                    toast.error('Old Password is incorrect. Please try again.')
                } finally {

                }
            } else {
                toast.error('New Password must match Confirm New Password. Please try again.')
            }

        } else {
            toast.error('New Password must be at least five characters long.')
        }

        await dispatch(endLoadingAction())
    }

    useEffect(() => {
        if (!changeMobilePhoneFormValues)
            return
        if (multiFactorGuid && multiFactorGuid.trim() && multiFactorGuidExpireDate > new Date())
            updateMobilePhone(multiFactorGuid)
        else
            setMfaVerifiedCallbackType(MfaCallbackTypeEnum.ChangeMobileNumber)
    }, [changeMobilePhoneFormValues])

    const validateMobilePhone = async (values: IChangeMobileNumber) => {
        setChangeMobilePhoneFormValues({ ...values })
    }

    //Actually updates password after MFA is successful
    const updateMobilePhone = async (multiFactorGuid: string) => {
        const formData = changeMobilePhoneFormValues
        if (!formData)
            return

        await dispatch(startLoadingAction())
        try {

            let response = await V1ApiUsersClient.updateUserMobilePhone(new UpdateUserMobilePhoneDto({
                mobilePhoneNumber: formData.mobilePhoneNumber,
                multiFactorGuid
            }))

            if (response.success) {
                toast.success('Mobile phone successfully changed.')
                setMfaVerifiedCallbackType(undefined)
            }

        } catch (e) {
            toast.responseErrors(e)
        } finally {
            await dispatch(endLoadingAction())
        }
    }

    const deleteMfaSetup = async (multiFactorGuid: string, multiFactorType: MultiFactorTypeEnum) => {
        try {
            await dispatch(startLoadingAction())
            setAlert({ variant: 'info', messages: [] })
            let response = await V1ApiUsersClient.deleteMultiFactorSetup(
                new DeleteMultiFactorSetupDto({
                    multiFactorType,
                    multiFactorGuid
                }))
            if (response.success)
                toast.success('Google Authenticator disabled successfully.')

            setTimeout(() => fetch(), 500)
        }
        catch (e) {
            toast.responseErrors(e)
        }
        finally {
            await dispatch(endLoadingAction())
        }
    }

    const mfaVerifiedCallback = async (dto: MultiFactorVerifiedDto) => {
        switch (mfaVerifiedCallbackType) {
            case MfaCallbackTypeEnum.Password:
                await updatePassword(dto.multiFactorGuid)
                break
            case MfaCallbackTypeEnum.DeleteGoogle:
                await deleteMfaSetup(dto.multiFactorGuid, MultiFactorTypeEnum.Google)
                break
            case MfaCallbackTypeEnum.ChangeMobileNumber:
                await updateMobilePhone(dto.multiFactorGuid)
                break
        }
    }

    const MultiFactorSetupHandler = (selectedMultiFactor: UserMultiFactorSettingsJoinedDto) => {
        switch (selectedMultiFactor.multiFactorTypeId) {
            case MultiFactorTypeEnum.Google:
                return <div>
                    {
                        selectedMultiFactor.enabled ?
                            <div>
                                <MessageAlert alert={{ variant: 'info', messages: ['Google Authenticator is already setup.'] }} />
                                <Button
                                    variant='primary'
                                    className='mt-2'
                                    type='submit'
                                    disabled={loading}
                                    onClick={() => setMfaVerifiedCallbackType(MfaCallbackTypeEnum.DeleteGoogle)}>
                                    <FontAwesomeIcon icon={['fas', 'trash']} /> Disable
                                </Button>
                            </div>
                            : <div>
                                {sms2faRequired && <MessageAlert alert={{ variant: 'info', messages: ['SMS 2FA is required to complete the Google Authenticator setup.'] }} />}

                                <GoogleAuthenticatorSetupForm
                                    onVerificationRequired={() => setSms2faRequired(true)}
                                    onSetup={async () => { await fetch() }}
                                    onVerified={mfaVerifiedCallback}
                                />
                            </div>
                    }
                </div>

            case MultiFactorTypeEnum.Sms:
                return <SmsMultiFactorSetupForm
                    onSetup={(e: IChangeMobileNumber) => validateMobilePhone(e)}
                />
            default:
                return <></>
        }
    }

    if (mfaVerifiedCallbackType) {
        return (
            <div>
                <PageHeader title={title} />
                <MessageAlert alert={alert} />
                <MultiFactorSelectorForm
                    onVerified={mfaVerifiedCallback}
                    onCancel={() => setMfaVerifiedCallbackType(undefined)}
                    message={MFA_LOGIN_SETTINGS_MESSAGE}
                />
            </div>
        )
    }

    return (
        <div>
            <PageHeader title={title} />
            <MessageAlert alert={alert} />
            <div className='bg-light p-3 mb-2 border rounded shadow-sm'>
                <h5>Change Password</h5>
                <hr />
                {!loaded
                    ? <FormSkeleton style={{ height: '80px' }} />
                    : <Form
                        ref={updatePasswordFormRef}
                        onSubmit={(e) => validateUpdatePassword(e as IChangePassword)}
                        render={(formRenderProps) => (
                            <FormElement>
                                <div>
                                    <Row className='mb-4'>
                                        <Col lg={3}>
                                            <Field
                                                name='oldPassword'
                                                id='oldPassword'
                                                component={FormInput}
                                                type='password'
                                                label='Old Password:'
                                                validator={RequiredValidator}
                                            />

                                        </Col>
                                        <Col lg={3}>
                                            <Field
                                                name='newPassword'
                                                id='newPassword'
                                                component={FormInput}
                                                type='password'
                                                label='New Password:'
                                                validator={RequiredValidator}
                                            />

                                        </Col>
                                        <Col lg={3}>
                                            <Field
                                                name='confirmNewPassword'
                                                id='confirmNewPassword'
                                                component={FormInput}
                                                type='password'
                                                label='Confirm New Password:'
                                                validator={RequiredValidator}
                                            />

                                        </Col>
                                        <Col lg={3} style={{ alignSelf: 'center' }}>
                                            <SaveButton
                                                className='btn btn-primary btn-lg mt-4'
                                                type='submit'
                                                disabled={!formRenderProps.allowSubmit || loading}
                                            >
                                                Save
                                            </SaveButton>
                                        </Col>
                                    </Row>
                                </div>
                            </FormElement>
                        )}
                    />
                }
            </div>

            <div className='bg-light p-3 mb-2 border rounded shadow-sm'>
                <Row>
                    <Col xs={6}>
                        <h5>Two-Factor Options</h5>
                    </Col>
                    <Col xs={6}>
                        {userMultiFactorSettings && userMultiFactorSettings.length > 1 && selectedMultiFactor && selectedMultiFactor.multiFactorTypeId !== MultiFactorTypeEnum.Undefined
                            && <span className='float-end'>
                                <DefaultButton
                                    className='btn-sm mb-2'
                                    variant='default'
                                    disabled={loading}
                                    onClick={back}>
                                    <FontAwesomeIcon icon={['fas', 'reply']} />{' '}Back
                                </DefaultButton>
                            </span>
                        }
                    </Col>
                </Row>
                <hr />

                {!loaded
                    ? <FormSkeleton style={{ height: '80px' }} />
                    : <div>
                        {!selectedMultiFactor || selectedMultiFactor.multiFactorTypeId === MultiFactorTypeEnum.Undefined
                            ? <ListGroup>
                                {userMultiFactorSettings.map(s => (
                                    <ListGroup.Item
                                        key={s.multiFactorTypeId}
                                        className='cursor-pointer'
                                        onClick={() => setSelectedMultiFactor(s)}>
                                        <div>
                                            <img
                                                src={s.logoUrl}
                                                style={{ height: '50px' }}
                                                draggable={false}
                                            />{' '}
                                            {s.name} {s.recommended && <Badge bg={'success'}>Recommended</Badge>}
                                        </div>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                            : <div>
                                {MultiFactorSetupHandler(selectedMultiFactor)}
                            </div>
                        }
                    </div>
                }
            </div>
        </div>
    )
}
export default LoginSettingsPage
